import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Typography, Button, Alert, Tooltip, Drawer, Divider,
  Modal, Input, Space, Select, Spin, Image, Tag, DatePicker,
  Popover, Badge, message,
} from 'antd';
import {
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  FilePdfOutlined, LinkOutlined, UserOutlined, FileTextOutlined,
  CreditCardOutlined, PaperClipOutlined, ArrowsAltOutlined, EyeOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { api } from '../../services/api';
import dayjs from 'dayjs';
import { SkStyleTag, skeletonColumns, skeletonRows } from '../../shared/components/TableSkeleton';
import { useClient } from '../../context/ClientContext';
import { EstadoPagoChip } from '../../shared/components/EstadoPagoChip';
import { EstadoPedidoChip } from '../../shared/components/EstadoPedidoChip';
import { hexAlpha } from '../../utils/colorUtils';
import { useAuth } from '../../context/AuthContext';

const { Text } = Typography;
const CODIGOS_ACCIONABLES = ['pending', 'pending_approval', 'in_process'];

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const CircleBtn = ({ icon, onClick, title, danger }) => (
  <Tooltip title={title}>
    <button type="button" onClick={onClick} style={{
      width: 32, height: 32, border: `1px solid ${danger ? '#fca5a5' : '#e5e7eb'}`,
      borderRadius: '50%', background: '#fff', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      color: danger ? '#ef4444' : '#374151', fontSize: 14, flexShrink: 0,
      transition: 'border-color 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = danger ? '#ef4444' : '#374151'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = danger ? '#fca5a5' : '#e5e7eb'; }}
    >{icon}</button>
  </Tooltip>
);

export const Cobros = () => {
  const navigate = useNavigate();
  const { clientId, theme , clientPath} = useClient();
  // El tesorero ve solo lo que entró a sus cuentas (lo filtra el backend) y
  // aprueba o rechaza, pero no abre el pedido ni reasigna la cuenta.
  const { user } = useAuth();
  const esTesorero = user?.id_rol === 4;
  const primary = theme?.primaryColor || '#0f172a';
  const primaryText = theme?.primaryTextColor || '#fff';
  const [pedidos, setPedidos]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [filtro, setFiltro]     = useState(undefined);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [desde, setDesde] = useState(null);
  const [hasta, setHasta] = useState(dayjs());
  const [estados, setEstados]   = useState([]);
  const [metodoPago, setMetodoPago] = useState(undefined);
  const [metodos, setMetodos]   = useState([]);
  const [metodoCobro, setMetodoCobro] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [blobUrls, setBlobUrls] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [motivo, setMotivo]         = useState('');
  const [accionando, setAccionando] = useState(null);
  const [filtrosOpen, setFiltrosOpen] = useState(false);

  const filtrosActivos = [filtro, filtroCliente, desde, metodoPago].filter(Boolean).length;

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.admin.getCobros(filtro, filtroCliente, desde?.format('YYYY-MM-DD'), hasta?.format('YYYY-MM-DD'), metodoPago);
      if (res.status === 'success') setPedidos(res.data || []);
      else setError(res.message || 'Error al cargar cobros');
    } catch { setError('Error al conectar con el servidor'); }
    finally { setLoading(false); }
  }, [filtro, filtroCliente, desde, hasta, metodoPago]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.pagos.getMetodos().then(res => { if (res.status === 'success') setMetodos(res.data || []); });
  }, []);
  useEffect(() => {
    api.pagos.getEstados().then(res => { if (res.status === 'success') setEstados(res.data || []); });
  }, []);
  useEffect(() => {
    if (!detail) return;
    detail.adjuntos?.forEach(async (adj) => {
      if (blobUrls[adj.id]) return;
      const url = await api.pagos.getAdjuntoBlob(adj.id);
      if (url) setBlobUrls(prev => ({ ...prev, [adj.id]: url }));
    });
  }, [detail]); // eslint-disable-line

  const esPdf = (n) => n?.toLowerCase().endsWith('.pdf');

  const handleAprobar = async (pedidoId) => {
    setAccionando(pedidoId);
    const res = await api.admin.aprobarPago(pedidoId, metodoCobro);
    setAccionando(null);
    if (res.status === 'success') { setDetail(null); cargar(); }
    else alert(res.message || 'Error al aprobar');
  };

  const handleRechazar = async () => {
    const pedidoId = detail?.id;
    setAccionando(pedidoId);
    const res = await api.admin.rechazarPago(pedidoId, motivo);
    setAccionando(null);
    setRejectOpen(false); setMotivo('');
    if (res.status === 'success') { setDetail(null); cargar(); }
    else alert(res.message || 'Error al rechazar');
  };

  const puedeAccionar = detail ? CODIGOS_ACCIONABLES.includes(detail.estado_pago_codigo) : false;

  const estadoOptions = estados.map(e => ({ label: e.nombre, value: e.codigo }));
  const metodoOptions = metodos.map(m => ({ label: m.nombre, value: m.codigo }));
  // Un pedido cargado a mano llega sin medio de pago: hay que decir con que se
  // cobro, porque de ahi sale el ajuste de la pasarela.
  const faltaMetodo = puedeAccionar && !detail?.metodo_pago;
  // Las cuentas a las que se puede haber transferido, para conciliar.
  const transferencia = metodos.find(m => m.codigo === 'transferencia');
  const esTransferencia = !!transferencia && detail?.payment?.id_metodo_pago === transferencia.id;

  const corregirCuenta = async (idCuenta) => {
    const res = await api.admin.corregirCuentaPago(detail.id, idCuenta);
    if (res.status === 'success') {
      const c = (transferencia?.cuentas || []).find(x => x.id === idCuenta);
      setDetail(d => ({ ...d, cuenta: c?.nombre || d.cuenta, payment: { ...d.payment, id_cuenta: idCuenta } }));
      message.success('Cuenta corregida');
      cargar();
    } else {
      message.error(res.message || 'No se pudo corregir');
    }
  };

  const columns = [
    {
      title: 'Cliente', key: 'cliente', minWidth: 200,
      render: (_, p) => {
        const nombre = p.nombre_cliente || '—';
        const initials = nombre.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 8, flexShrink: 0,
              background: hexAlpha(primary, 0.10),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: primary, fontWeight: 700, fontSize: 13,
            }}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: primary }}>{nombre}</div>
              {p.email_cliente && <div style={{ fontSize: 12, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email_cliente}</div>}
            </div>
          </div>
        );
      },
    },
    {
      title: '#', dataIndex: 'id', key: 'id', width: 70,
      render: (id) => <Text type="secondary" style={{ fontSize: 13 }}>#{id}</Text>,
    },
    {
      title: 'Fecha', dataIndex: 'fecha_alta', key: 'fecha', width: 120,
      render: (v) => <Text style={{ fontSize: 13 }}>{v ? new Date(v).toLocaleDateString('es-AR') : '—'}</Text>,
    },
    {
      title: 'Método', dataIndex: 'metodo_pago', key: 'metodo', width: 130,
      render: (v) => <Text type="secondary" style={{ fontSize: 13 }}>{v || '—'}</Text>,
    },
    {
      title: 'Total', dataIndex: 'total', key: 'total', width: 120,
      render: (v) => <Text strong style={{ fontSize: 14 }}>$ {Number(v).toFixed(2)}</Text>,
    },
    {
      title: 'Estado pago', key: 'estado_pago', width: 140,
      render: (_, p) => <EstadoPagoChip nombre={p.estado_pago_nombre} descripcion={p.estado_pago_desc} color={p.estado_pago_color} />,
    },
    {
      title: '', key: 'acciones', width: 60, align: 'center',
      render: (_, p) => (
        <CircleBtn icon={<EyeOutlined />} title="Ver detalle" onClick={(e) => { e.stopPropagation(); setDetail(pedidos.find(x => x.id === p.id) || p); }} />
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Cobros</Text>
        <Space>
          <Popover
            open={filtrosOpen}
            onOpenChange={setFiltrosOpen}
            trigger="click"
            placement="bottomRight"
            content={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: 280 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <DatePicker
                    value={desde}
                    onChange={(v) => setDesde(v)}
                    placeholder="Desde"
                    format="DD/MM/YYYY"
                    allowClear
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: '#999' }}>—</span>
                  <DatePicker
                    value={hasta}
                    onChange={(v) => setHasta(v)}
                    placeholder="Hasta"
                    format="DD/MM/YYYY"
                    allowClear
                    style={{ flex: 1 }}
                  />
                </div>
                <Input.Search
                  key={filtroCliente}
                  placeholder="Buscar por cliente..."
                  defaultValue={filtroCliente}
                  allowClear
                  onSearch={(v) => setFiltroCliente(v)}
                />
                <Select
                  value={filtro}
                  onChange={(v) => setFiltro(v)}
                  options={estadoOptions}
                  placeholder="Filtrar por estado"
                  style={{ width: '100%' }}
                  allowClear
                />
                <Select
                  value={metodoPago}
                  onChange={(v) => setMetodoPago(v)}
                  options={metodoOptions}
                  placeholder="Filtrar por método de pago"
                  style={{ width: '100%' }}
                  allowClear
                />
              </div>
            }
          >
            <Badge count={filtrosActivos} size="small">
              <Button icon={<FilterOutlined />}>Filtros</Button>
            </Badge>
          </Popover>
          <Tooltip title="Actualizar">
            <Button type="text" icon={<ReloadOutlined />} onClick={cargar} loading={loading} />
          </Tooltip>
        </Space>
      </div>

      {filtrosActivos > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          {desde && (
            <Tag closable onClose={() => setDesde(null)}>
              Desde: {desde.format('DD/MM/YYYY')}
            </Tag>
          )}
          {filtroCliente && (
            <Tag closable onClose={() => setFiltroCliente('')}>
              Cliente: {filtroCliente}
            </Tag>
          )}
          {filtro && (
            <Tag closable onClose={() => setFiltro(undefined)}>
              Estado: {estadoOptions.find(o => o.value === filtro)?.label || filtro}
            </Tag>
          )}
          {metodoPago && (
            <Tag closable onClose={() => setMetodoPago(undefined)}>
              Método: {metodoOptions.find(o => o.value === metodoPago)?.label || metodoPago}
            </Tag>
          )}
        </div>
      )}

      {!loading && !error && (
        <Text type="secondary" style={{ fontSize: 13, marginBottom: 12, display: 'block' }}>
          {pedidos.length} cobro{pedidos.length !== 1 ? 's' : ''}
        </Text>
      )}

      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

      <SkStyleTag />
      <Table
        columns={loading ? skeletonColumns(columns, { avatarIndex: 0, twoLineIndexes: [0] }) : columns}
        dataSource={loading ? skeletonRows(9) : pedidos}
        rowKey={loading ? 'key' : 'id'}
        size="middle"
        scroll={{ x: 'max-content' }}
        onRow={loading ? undefined : (p) => ({ onClick: () => setDetail(pedidos.find(x => x.id === p.id) || p), style: { cursor: 'pointer' } })}
        locale={{ emptyText: 'No hay cobros en este estado.' }}
        pagination={loading ? false : { pageSize: 20, showSizeChanger: false, style: { padding: '8px 16px' } }}
        style={{ background: '#fff', borderRadius: 8 }}
      />

      {!loading && pedidos.length > 0 && (
        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 13, textAlign: 'center' }}>
          Mostrando 1–{pedidos.length} de {pedidos.length} cobro{pedidos.length !== 1 ? 's' : ''}
        </Text>
      )}

      {/* Drawer de detalle */}
      <Drawer
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        placement="right"
        width={480}
        title={
          detail && (
            <Space>
              <span>Pedido #{detail.id}</span>
              {!esTesorero && (
                <Tooltip title="Ver detalle completo">
                  <Button type="text" size="small" icon={<ArrowsAltOutlined />}
                    onClick={() => navigate(clientPath(`/admin/pedidos/${detail?.id}`))} />
                </Tooltip>
              )}
            </Space>
          )
        }
        footer={puedeAccionar && (
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            {faltaMetodo && (
              <Select
                placeholder="¿Con qué se cobró?"
                style={{ width: '100%' }}
                value={metodoCobro}
                onChange={setMetodoCobro}
                options={metodos.map(m => ({
                  value: m.id,
                  label: m.descuento ? `${m.nombre} (${m.descuento}%)` : m.nombre,
                }))}
              />
            )}
          <Space style={{ width: '100%', justifyContent: 'stretch' }}>
            <Button danger icon={<CloseCircleOutlined />} block
              disabled={accionando === detail?.id}
              onClick={() => setRejectOpen(true)}>
              Rechazar
            </Button>
            <Button type="primary" icon={<CheckCircleOutlined />} block
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
              loading={accionando === detail?.id}
              onClick={() => handleAprobar(detail?.id)}>
              Aprobar
            </Button>
          </Space>
          </Space>
        )}
      >
        {detail && (
          <div>
            {/* Estado actual */}
            <div style={{ background: '#f8f9fa', border: '1px solid #e8e8e8', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
              <Space>
                <EstadoPagoChip nombre={detail.estado_pago_nombre} descripcion={detail.estado_pago_desc} color={detail.estado_pago_color} />
                <Text type="secondary" style={{ fontSize: 13 }}>Estado actual del pago</Text>
              </Space>
            </div>

            {/* Cliente */}
            <SectionTitle icon={<UserOutlined />} title="Cliente" />
            <InfoRow label="Nombre" value={detail.nombre_cliente || '—'} />
            <InfoRow label="Email"  value={detail.email_cliente  || '—'} />

            <Divider style={{ margin: '16px 0' }} />

            {/* Pedido */}
            <SectionTitle icon={<FileTextOutlined />} title="Pedido" />
            <InfoRow label="ID"     value={`#${detail.id}`} />
            <InfoRow label="Total"  value={`$ ${Number(detail.total).toFixed(2)} ${detail.currency || ''}`} bold />
            <InfoRow label="Fecha"  value={fmt(detail.fecha_alta)} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Estado</Text>
              <EstadoPedidoChip nombre={detail.estado_nombre} descripcion={detail.estado_desc} color={detail.estado_color} />
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Pago */}
            <SectionTitle icon={<CreditCardOutlined />} title="Pago" />
            <InfoRow label="Método"      value={detail.metodo_pago || '—'} />
            {esTransferencia && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', gap: 12 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>Transferido a</Text>
                <div style={{ textAlign: 'right' }}>
                  {/* Se corrige al conciliar; resuelto el pago, la cuenta queda fija. */}
                  {esTesorero || !puedeAccionar ? <Text style={{ fontSize: 13 }}>{detail.cuenta || 'Sin indicar'}</Text> : <Select
                    size="small"
                    placeholder="Sin indicar"
                    value={detail.payment?.id_cuenta || undefined}
                    onChange={corregirCuenta}
                    options={(transferencia.cuentas || []).map(c => ({ value: c.id, label: c.nombre }))}
                    style={{ minWidth: 180 }}
                    aria-label="Cuenta a la que se transfirió"
                  />}
                  {detail.tesorero && <div><Text type="secondary" style={{ fontSize: 12 }}>la maneja {detail.tesorero}</Text></div>}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Estado pago</Text>
              <EstadoPagoChip nombre={detail.estado_pago_nombre} descripcion={detail.estado_pago_desc} color={detail.estado_pago_color} />
            </div>
            {detail.payment?.currency_paid && detail.payment.currency_paid !== detail.payment.currency && (
              <InfoRow label={`Cobrado en ${detail.payment.currency_paid}`} value={enMoneda(detail.payment)} />
            )}
            <InfoRow label="Registrado"  value={fmt(detail.payment?.created_at)} />
            {detail.payment?.aprobado && <InfoRow label="Aprobado" value={decision(detail.payment.aprobado, detail.aprobado_por)} />}
            {detail.payment?.rechazado && <InfoRow label="Rechazado" value={decision(detail.payment.rechazado, detail.rechazado_por)} />}
            {detail.payment?.updated_at && <InfoRow label="Actualizado" value={fmt(detail.payment.updated_at)} />}
            {detail.payment?.id_transaccion && <InfoRow label="ID transacción" value={detail.payment.id_transaccion} mono />}

            {/* Comprobantes */}
            {detail.adjuntos?.length > 0 && (
              <>
                <Divider style={{ margin: '16px 0' }} />
                <SectionTitle icon={<PaperClipOutlined />} title={`Comprobantes (${detail.adjuntos.length})`} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                  {detail.adjuntos.map((adj) => {
                    const url = blobUrls[adj.id];
                    const pdf = esPdf(adj.adjunto);
                    return (
                      <div key={adj.id} style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
                        {!pdf && url && (
                          <img src={url} alt={adj.adjunto}
                            onClick={() => setLightbox(url)}
                            style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                          />
                        )}
                        <div style={{ padding: '8px 12px', background: '#fafafa', display: 'flex', alignItems: 'center', gap: 8 }}>
                          {pdf && <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 16, flexShrink: 0 }} />}
                          <Text ellipsis style={{ flex: 1, fontSize: 12 }}>{adj.adjunto}</Text>
                          <Tooltip title="Abrir en nueva pestaña">
                            <Button type="text" size="small" icon={url ? <LinkOutlined /> : <Spin size="small" />}
                              disabled={!url} onClick={() => url && window.open(url, '_blank')} />
                          </Tooltip>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </Drawer>

      {/* Lightbox */}
      <Image
        style={{ display: 'none' }}
        preview={{
          visible: Boolean(lightbox),
          src: lightbox,
          onVisibleChange: (v) => { if (!v) setLightbox(null); },
        }}
      />

      {/* Modal rechazo */}
      <Modal
        open={rejectOpen}
        title={`Rechazar pago — Pedido #${detail?.id}`}
        onCancel={() => { setRejectOpen(false); setMotivo(''); }}
        onOk={handleRechazar}
        okText="Rechazar"
        okButtonProps={{ danger: true, loading: accionando === detail?.id }}
        cancelText="Cancelar"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          El cliente verá este motivo en el detalle de su pedido.
        </Text>
        <Input.TextArea
          rows={3}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ej: El monto no coincide, el comprobante no es legible..."
        />
      </Modal>
    </div>
  );
};

// Lo cobrado en otra moneda que la del pedido (PayPal, en dólares), con la
// cotización que quedó congelada. Sin amount_paid todavía no se acreditó.
const enMoneda = (p) => `${p.currency_paid} ${p.amount_paid != null ? Number(p.amount_paid).toFixed(2) : '—'}${p.exchange_rate ? ` · a $ ${Number(p.exchange_rate).toLocaleString('es-AR')}` : ''}`;

// Sin persona es que lo resolvió la pasarela sola (por ejemplo, Mercado Pago).
const decision = (fecha, quien) => `${fmt(fecha)} · ${quien || 'la pasarela'}`;

const SectionTitle = ({ icon, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
    <span style={{ color: '#888', display: 'flex' }}>{icon}</span>
    <Text strong style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#888' }}>{title}</Text>
  </div>
);

const InfoRow = ({ label, value, bold, mono }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '4px 0' }}>
    <Text type="secondary" style={{ fontSize: 13, flexShrink: 0, marginRight: 16 }}>{label}</Text>
    <Text strong={bold} style={{ fontSize: 13, textAlign: 'right', fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>
      {value}
    </Text>
  </div>
);
